import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle, Shield, DollarSign, Users, TrendingUp, Target,
  Activity, Loader2, Download, RefreshCw, Clock,
  BarChart3, FileText, Building2, Zap,
  AlertCircle, CheckCircle2, HelpCircle, Ban, Info, X, FileSpreadsheet,
  Calculator, ChevronDown, ChevronUp, ExternalLink
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadialBarChart, RadialBar,
} from "recharts";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const CHART_TOOLTIP = { borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12 };
const SEVERITY_CONFIG = {
  critical: { color: "#ef4444", bg: "bg-red-50", text: "text-red-700", border: "border-red-200", icon: Ban },
  high: { color: "#f97316", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", icon: AlertTriangle },
  medium: { color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: AlertCircle },
  low: { color: "#10b981", bg: "bg-green-50", text: "text-green-700", border: "border-green-200", icon: CheckCircle2 },
};

const fmtCurrency = (val) => val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M` : val >= 1000 ? `$${(val / 1000).toFixed(0)}K` : `$${val}`;

const FormulaCard = ({ formula, isLast }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("border-l-2 border-primary/30 pl-4 py-2", !isLast && "mb-2")} data-testid={`formula-${formula.label?.replace(/\s+/g, '-').toLowerCase()}`}>
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left group">
        <div className="flex items-center gap-2">
          <Calculator className="w-3.5 h-3.5 text-primary/60" />
          <span className="text-sm font-semibold text-slate-800">{formula.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-primary">{fmtCurrency(formula.result || 0)}</span>
          {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="mt-2 space-y-1.5 text-xs">
              <div className="flex items-start gap-1.5"><span className="text-slate-400 w-16 shrink-0">Formula:</span><span className="text-slate-600">{formula.formula}</span></div>
              <div className="flex items-start gap-1.5 bg-blue-50 rounded-md px-2 py-1.5 border border-blue-100">
                <span className="text-blue-500 w-16 shrink-0 font-medium">Calc:</span><span className="text-blue-800 font-mono text-[11px]">{formula.calculation}</span>
              </div>
              {formula.source && <div className="flex items-start gap-1.5"><ExternalLink className="w-3 h-3 text-slate-300 mt-0.5 shrink-0" /><span className="text-slate-400 italic">{formula.source}</span></div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RiskAssessmentPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showExplanation, setShowExplanation] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get("/risk-assessment");
      setData(res.data);
    } catch (e) { toast.error("Failed to load risk assessment"); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await api.get(`/risk-assessment/export/${format}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `odesie_risk_report.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Risk report exported as ${format.toUpperCase()}`);
    } catch (e) { toast.error(`Export failed: ${e.message}`); }
    finally { setExporting(false); }
  };

  if (loading) return (
    <AdminLayout title="Risk Assessment" variant={variant}>
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Analyzing risk data...</p>
      </div>
    </AdminLayout>
  );

  if (!data) return null;

  const o = data.overall;
  const fin = data.financial_impact || {};
  const depts = data.department_risks || [];
  const explanation = data.explanation_breakdown || {};
  const formulas = explanation.formulas || {};
  const sc = SEVERITY_CONFIG[o.severity] || SEVERITY_CONFIG.medium;
  const SeverityIcon = sc.icon;

  const riskGaugeData = [{ name: "Risk", value: o.risk_score, fill: sc.color }];
  const costBreakdown = [
    { name: "OSHA Fines", value: fin.osha_fine_exposure || 0, fill: "#ef4444" },
    { name: "Productivity", value: fin.productivity_loss || 0, fill: "#f59e0b" },
    { name: "Litigation", value: fin.litigation_exposure || 0, fill: "#8b5cf6" },
  ].filter(c => c.value > 0);

  return (
    <AdminLayout title="Risk Assessment" variant={variant}>
      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show" data-testid="risk-assessment-page">
        {/* Header */}
        <motion.div variants={item} className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">Odesie AI Analysis</p>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Risk & Financial Assessment</h1>
            <p className="text-sm text-slate-500 mt-1">Quantified risk exposure with transparent calculation engine</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={() => fetchData(true)} disabled={refreshing} data-testid="refresh-btn">
              <RefreshCw className={cn("w-3.5 h-3.5", refreshing && "animate-spin")} /> Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs gap-1.5" disabled={exporting} data-testid="export-dropdown-btn">
                  <Download className="w-3.5 h-3.5" /> {exporting ? "Exporting..." : "Export"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="export-pdf-btn">
                  <FileText className="w-3.5 h-3.5 mr-2" /> Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="export-csv-btn">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.div>

        {/* Financial Impact Hero */}
        <motion.div variants={item}>
          <Card className={cn("border-2 shadow-none", sc.border)}>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Risk Score Gauge */}
                <div className="flex flex-col items-center justify-center">
                  <ResponsiveContainer width={160} height={100}>
                    <RadialBarChart cx="50%" cy="100%" innerRadius="65%" outerRadius="100%" barSize={14} data={riskGaugeData} startAngle={180} endAngle={0}>
                      <RadialBar background clockWise dataKey="value" cornerRadius={7} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <div className="text-center -mt-5">
                    <p className="text-3xl font-black" style={{ color: sc.color }}>{o.risk_score}</p>
                    <Badge className={cn("text-xs mt-1 gap-1 border", sc.bg, sc.text, sc.border)}>
                      <SeverityIcon className="w-3 h-3" /> {o.severity.toUpperCase()} RISK
                    </Badge>
                  </div>
                </div>

                {/* Total Financial Exposure */}
                <div className="lg:col-span-2 flex flex-col justify-center">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Financial Exposure</p>
                    <button onClick={() => setShowExplanation(true)} className="text-primary/60 hover:text-primary transition-colors" data-testid="open-explanation-btn" title="How we calculated this">
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-4xl font-black text-slate-900 tracking-tight mt-1" data-testid="total-risk-cost">
                    {fmtCurrency(fin.total_estimated_risk || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Estimated annual risk cost based on compliance gaps, training deficiencies, and regulatory exposure</p>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="p-2.5 rounded-lg bg-red-50 border border-red-100 cursor-pointer hover:border-red-300 transition-colors" onClick={() => setShowExplanation(true)}>
                      <p className="text-xs font-bold text-red-700">{fmtCurrency(fin.osha_fine_exposure || 0)}</p>
                      <p className="text-[10px] text-red-600">OSHA Fine Exposure</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-100 cursor-pointer hover:border-amber-300 transition-colors" onClick={() => setShowExplanation(true)}>
                      <p className="text-xs font-bold text-amber-700">{fmtCurrency(fin.productivity_loss || 0)}</p>
                      <p className="text-[10px] text-amber-600">Productivity Loss</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-violet-50 border border-violet-100 cursor-pointer hover:border-violet-300 transition-colors" onClick={() => setShowExplanation(true)}>
                      <p className="text-xs font-bold text-violet-700">{fmtCurrency(fin.litigation_exposure || 0)}</p>
                      <p className="text-[10px] text-violet-600">Litigation Risk</p>
                    </div>
                  </div>
                </div>

                {/* Potential Savings */}
                <div className="flex flex-col justify-center">
                  <Card className="border shadow-none bg-emerald-50/50">
                    <CardContent className="p-4 text-center">
                      <Zap className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                      <p className="text-2xl font-black text-emerald-700">{fmtCurrency(fin.potential_savings || 0)}</p>
                      <p className="text-[10px] text-emerald-600 font-medium">Potential Savings</p>
                      <p className="text-[10px] text-emerald-500 mt-1">by addressing top risks</p>
                    </CardContent>
                  </Card>
                  <div className="text-center mt-2">
                    <p className="text-xs text-muted-foreground">{fmtCurrency(fin.cost_per_employee || 0)}/employee</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Understand This Calculation Banner */}
        <motion.div variants={item}>
          <button
            onClick={() => setShowExplanation(true)}
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50/60 hover:bg-blue-50 transition-colors text-left"
            data-testid="calculation-banner"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <Info className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-blue-800">Understand This Calculation</p>
              <p className="text-[10px] text-blue-600">Click to see the exact formulas, data inputs, and regulatory sources behind every number on this page</p>
            </div>
            <ChevronDown className="w-4 h-4 text-blue-400 shrink-0" />
          </button>
        </motion.div>

        {/* Key Metrics */}
        <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Course Completion", value: `${o.completion_rate}%`, icon: Target, color: o.completion_rate >= 80 ? "#10b981" : o.completion_rate >= 50 ? "#f59e0b" : "#ef4444" },
            { label: "Compliance Rate", value: `${o.compliance_rate}%`, icon: Shield, color: o.compliance_rate >= 90 ? "#10b981" : o.compliance_rate >= 70 ? "#f59e0b" : "#ef4444" },
            { label: "Overdue Items", value: o.overdue_compliance, icon: Clock, color: o.overdue_compliance > 0 ? "#ef4444" : "#10b981" },
            { label: "Active Learners", value: o.total_learners, icon: Users, color: "#3b82f6" },
          ].map((m, i) => (
            <Card key={i} className="border shadow-none"><CardContent className="p-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: m.color + "12" }}><m.icon className="w-4 h-4" style={{ color: m.color }} /></div>
              <div><p className="text-lg font-bold leading-none" style={{ color: m.color }}>{m.value}</p><p className="text-[10px] text-muted-foreground">{m.label}</p></div>
            </CardContent></Card>
          ))}
        </motion.div>

        {/* Tabs */}
        <motion.div variants={item}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-muted/50 h-9">
              <TabsTrigger value="overview" className="text-xs gap-1.5" data-testid="tab-overview"><BarChart3 className="w-3.5 h-3.5" /> Department Risk</TabsTrigger>
              <TabsTrigger value="financial" className="text-xs gap-1.5" data-testid="tab-financial"><DollarSign className="w-3.5 h-3.5" /> Cost Analysis</TabsTrigger>
              <TabsTrigger value="ai" className="text-xs gap-1.5" data-testid="tab-ai"><Activity className="w-3.5 h-3.5" /> Odesie AI</TabsTrigger>
              <TabsTrigger value="methodology" className="text-xs gap-1.5" data-testid="tab-methodology"><Calculator className="w-3.5 h-3.5" /> Methodology</TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>

        {/* Department Risk Tab */}
        {activeTab === "overview" && (
          <motion.div variants={item} className="space-y-4">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Risk Score by Department</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={depts.slice(0, 10)} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="department" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} />
                    <Tooltip contentStyle={CHART_TOOLTIP} formatter={(val, name) => [name === "estimated_cost" ? fmtCurrency(val) : val, name === "estimated_cost" ? "Est. Cost" : name]} />
                    <Bar dataKey="risk_score" name="Risk Score" radius={[4, 4, 0, 0]}>
                      {depts.map((d, i) => <Cell key={i} fill={(SEVERITY_CONFIG[d.severity] || SEVERITY_CONFIG.medium).color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <div className="space-y-2">
              {depts.map((d, i) => {
                const dsc = SEVERITY_CONFIG[d.severity] || SEVERITY_CONFIG.medium;
                const DSIcon = dsc.icon;
                return (
                  <Card key={i} className={cn("border shadow-none border-l-4", dsc.border)} data-testid={`dept-risk-${d.department}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", dsc.bg)}><DSIcon className={cn("w-5 h-5", dsc.text)} /></div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold">{d.department}</h3>
                              <Badge className={cn("text-[10px] border gap-0.5", dsc.bg, dsc.text, dsc.border)}>{d.severity.toUpperCase()}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{d.total_users} users &middot; {d.overdue_count} overdue items</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right"><p className="text-xl font-black" style={{ color: dsc.color }}>{d.risk_score}</p><p className="text-[10px] text-muted-foreground">Risk Score</p></div>
                          <div className="text-right"><p className="text-sm font-bold text-red-600">{fmtCurrency(d.estimated_cost || 0)}</p><p className="text-[10px] text-muted-foreground">Est. Cost</p></div>
                          <div className="hidden sm:block w-24">
                            <div className="flex items-center justify-between text-[10px] mb-1"><span className="text-muted-foreground">Completion</span><span className="font-semibold">{d.completion_rate}%</span></div>
                            <Progress value={d.completion_rate} className={cn("h-1.5", d.completion_rate < 50 ? "[&>div]:bg-red-500" : d.completion_rate < 80 ? "[&>div]:bg-amber-500" : "")} />
                            <div className="flex items-center justify-between text-[10px] mt-1.5 mb-0.5"><span className="text-muted-foreground">Compliance</span><span className="font-semibold">{d.compliance_rate}%</span></div>
                            <Progress value={d.compliance_rate} className={cn("h-1.5", d.compliance_rate < 70 ? "[&>div]:bg-red-500" : d.compliance_rate < 90 ? "[&>div]:bg-amber-500" : "")} />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {depts.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No department risk data. Assign learners to departments and create compliance assignments to see risk analysis.</p>}
            </div>
          </motion.div>
        )}

        {/* Financial Cost Analysis Tab */}
        {activeTab === "financial" && (
          <motion.div variants={item} className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Cost Breakdown</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5 flex items-center justify-center">
                  {costBreakdown.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={costBreakdown} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${fmtCurrency(value)}`}>
                          {costBreakdown.map((c, i) => <Cell key={i} fill={c.fill} />)}
                        </Pie>
                        <Tooltip contentStyle={CHART_TOOLTIP} formatter={(val) => fmtCurrency(val)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-muted-foreground py-12">No cost exposure detected</p>}
                </CardContent>
              </Card>
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Estimated Cost by Department</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={depts.filter(d => d.estimated_cost > 0).slice(0, 8)} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtCurrency} />
                      <YAxis type="category" dataKey="department" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip contentStyle={CHART_TOOLTIP} formatter={(val) => fmtCurrency(val)} />
                      <Bar dataKey="estimated_cost" name="Est. Cost" radius={[0, 4, 4, 0]}>
                        {depts.map((d, i) => <Cell key={i} fill={(SEVERITY_CONFIG[d.severity] || SEVERITY_CONFIG.medium).color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Cost Detail Cards with inline formulas */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: "osha_fine_exposure", icon: AlertTriangle, color: "red", label: "OSHA Fine Exposure", value: fin.osha_fine_exposure, ref: "29 CFR 1903.15" },
                { key: "productivity_loss", icon: TrendingUp, color: "amber", label: "Productivity Loss", value: fin.productivity_loss, ref: "ATD Research" },
                { key: "litigation_exposure", icon: Shield, color: "violet", label: "Litigation Exposure", value: fin.litigation_exposure, ref: "BLS Data" },
              ].map(({ key, icon: Icon, color, label, value, ref }) => {
                const f = formulas[key] || {};
                return (
                  <Card key={key} className={`border shadow-none border-l-4 border-l-${color}-400`} data-testid={`cost-card-${key}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2"><Icon className={`w-4 h-4 text-${color}-500`} /><span className={`text-xs font-semibold text-${color}-700`}>{label}</span></div>
                      <p className={`text-2xl font-black text-${color}-600`}>{fmtCurrency(value || 0)}</p>
                      {f.calculation && <p className="text-xs text-muted-foreground mt-1.5 font-mono bg-slate-50 rounded px-2 py-1 border">{f.calculation}</p>}
                      <p className={`text-[10px] text-${color}-500 mt-2 font-medium`}>{ref}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* ROI */}
            <Card className="border shadow-none bg-gradient-to-r from-emerald-50 to-green-50">
              <CardContent className="p-5 flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center shrink-0"><DollarSign className="w-7 h-7 text-emerald-600" /></div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-emerald-800">Estimated ROI from Risk Mitigation</h3>
                  <p className="text-xs text-emerald-700 mt-0.5">By addressing the top risks, your organization could save up to <strong>{fmtCurrency(fin.potential_savings || 0)}</strong> annually &mdash; a {fin.total_estimated_risk > 0 ? Math.round((fin.potential_savings / fin.total_estimated_risk) * 100) : 70}% reduction in total exposure.</p>
                  {formulas.potential_savings?.calculation && <p className="text-[10px] text-emerald-600 mt-1 font-mono">{formulas.potential_savings.calculation}</p>}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* AI Analysis Tab */}
        {activeTab === "ai" && (
          <motion.div variants={item} className="space-y-4" id="risk-ai-analysis">
            <Card className="border shadow-none bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center"><Shield className="w-4 h-4 text-primary" /></div>
                    <div>
                      <h3 className="text-sm font-bold text-white">Odesie AI Analysis</h3>
                      <p className="text-[10px] text-white/50">{data.generated_at ? new Date(data.generated_at).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" }) : new Date().toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="text-white border-white/20 hover:bg-white/10 text-xs gap-1"
                    onClick={() => {
                      const el = document.getElementById("risk-ai-content");
                      if (!el) return;
                      const html = `<html><head><title>Odesie AI Risk Analysis</title><style>body{font-family:system-ui,sans-serif;padding:40px;max-width:800px;margin:0 auto;color:#1e293b}h1{font-size:20px;color:#0f172a;border-bottom:2px solid #e2e8f0;padding-bottom:12px}h2{font-size:16px;color:#334155;margin-top:24px}p{font-size:13px;line-height:1.7;color:#475569}.meta{font-size:11px;color:#94a3b8;margin-top:24px;border-top:1px solid #e2e8f0;padding-top:12px}</style></head><body><h1>Odesie AI Risk Analysis</h1>${el.innerHTML}<div class="meta">Generated: ${data.generated_at ? new Date(data.generated_at).toLocaleString() : new Date().toLocaleString()} | Exported: ${new Date().toLocaleString()}</div></body></html>`;
                      const blob = new Blob([html], { type: "text/html" });
                      const url = URL.createObjectURL(blob);
                      const win = window.open(url, "_blank");
                      setTimeout(() => { win?.print(); URL.revokeObjectURL(url); }, 500);
                    }}
                    data-testid="export-ai-pdf"
                  ><Download className="w-3.5 h-3.5" /> Export PDF</Button>
                </div>
                <div id="risk-ai-content" className="prose prose-sm prose-invert max-w-none leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: (data.ai_analysis || "AI analysis not available.")
                      .replace(/### (.*?)(\n|$)/g, '<h3 style="font-size:15px;font-weight:700;color:#fff;margin:16px 0 8px">$1</h3>')
                      .replace(/## (.*?)(\n|$)/g, '<h2 style="font-size:17px;font-weight:700;color:#fff;margin:20px 0 10px">$1</h2>')
                      .replace(/# (.*?)(\n|$)/g, '<h1 style="font-size:20px;font-weight:700;color:#fff;margin:24px 0 12px">$1</h1>')
                      .replace(/\*\*(.*?)\*\*/g, '<strong style="color:#fff">$1</strong>')
                      .replace(/\n\n/g, '</p><p style="font-size:14px;line-height:1.8;color:rgba(255,255,255,0.85);margin:8px 0">')
                      .replace(/\n- (.*?)(?=\n|$)/g, '<li style="font-size:13px;color:rgba(255,255,255,0.8);margin:4px 0;padding-left:4px">$1</li>')
                      .replace(/(<li.*?<\/li>)+/g, (match) => `<ul style="margin:8px 0;padding-left:20px">${match}</ul>`)
                  }}
                />
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Data Sources</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5 space-y-2">
                {(data.data_sources || []).map((ds, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`data-source-${i}`}>
                    <div className="flex items-center gap-2.5"><FileText className="w-4 h-4 text-muted-foreground" /><div><p className="text-xs font-medium">{ds.source}</p><p className="text-[10px] text-muted-foreground">{ds.type}</p></div></div>
                    {ds.records != null && <Badge variant="outline" className="text-[10px]">{ds.records.toLocaleString()} records</Badge>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Methodology Tab */}
        {activeTab === "methodology" && (
          <motion.div variants={item} className="space-y-4">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><Calculator className="w-4 h-4 text-primary" /> Calculation Methodology</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5">
                <p className="text-sm text-slate-600 mb-4">{explanation.methodology || "Deterministic calculations using actual LMS data."}</p>
                <div className="space-y-1">
                  {Object.keys(formulas).map((key, idx) => (
                    <FormulaCard key={key} formula={formulas[key]} isLast={idx === Object.keys(formulas).length - 1} />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Risk Score Formula */}
            {explanation.risk_score_formula && (
              <Card className="border shadow-none border-l-4 border-l-primary">
                <CardContent className="p-4">
                  <h3 className="text-sm font-bold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> {explanation.risk_score_formula.label}</h3>
                  <p className="text-xs text-slate-500 mb-1">Formula: {explanation.risk_score_formula.formula}</p>
                  <p className="text-xs font-mono bg-blue-50 rounded px-3 py-2 border border-blue-100 text-blue-800">{explanation.risk_score_formula.calculation}</p>
                </CardContent>
              </Card>
            )}

            {/* Data inputs table */}
            {explanation.data_inputs && (
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Data Inputs Used</CardTitle></CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(explanation.data_inputs).map(([key, val]) => (
                      <div key={key} className="p-2.5 rounded-lg border bg-slate-50/50" data-testid={`data-input-${key}`}>
                        <p className="text-lg font-bold text-slate-900">{typeof val === "number" ? val.toLocaleString() : val}</p>
                        <p className="text-[10px] text-muted-foreground capitalize">{key.replace(/_/g, " ")}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Explanation Modal */}
      <Dialog open={showExplanation} onOpenChange={setShowExplanation}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="explanation-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Calculator className="w-5 h-5 text-primary" /> How We Calculated These Numbers
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground mb-4">{explanation.methodology || ""}</p>
          <div className="space-y-1">
            {Object.keys(formulas).map((key, idx) => (
              <FormulaCard key={key} formula={formulas[key]} isLast={idx === Object.keys(formulas).length - 1} />
            ))}
          </div>
          {explanation.risk_score_formula && (
            <div className="mt-4 p-3 rounded-lg border border-primary/20 bg-primary/5">
              <p className="text-xs font-bold text-primary mb-1">{explanation.risk_score_formula.label}</p>
              <p className="text-xs font-mono text-primary/80">{explanation.risk_score_formula.calculation}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default RiskAssessmentPage;
